import React from 'react';
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

function SavedSteps({
  steps,
  onDragEnd,
  onEditStep,
  onRemoveStep,
  onEditApiCall,
  onRemoveApiCall,
  formatStepNameForDisplay,
  formatApiCallNameForDisplay
}) {
  // Resolve tokens like {{step:<id>.value}} to actual values for display only
  const resolveStepReferences = (text) => {
    if (!text) return '';
    return String(text).replace(/\{\{\s*step:([^}]+?)\.value\s*\}\}/g, (match, stepId) => {
      const referenced = (steps || []).find(s => s.id === stepId);
      if (!referenced) return match;
      const value = referenced.secret ? '••••••••' : referenced.value;
      return value !== undefined && value !== null ? String(value) : '';
    });
  };

  return (
    <div className="border rounded-lg overflow-hidden mb-4">
      <div className="bg-gray-50 px-4 py-2 border-b">
        <h3 className="text-sm font-medium text-gray-700">Saved Steps</h3>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="steps-list">
          {(provided) => (
            <div 
              className="divide-y overflow-x-auto" 
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {steps.map((item, index) => {
                const displayItem = item.action === 'assert'
                  ? { ...item, value: resolveStepReferences(item.value) }
                  : item;
                return (
                  <Draggable
                    key={item.isApiCall ? `api-${index}` : `step-${index}`} 
                    draggableId={item.isApiCall ? `api-${index}` : `step-${index}`} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 ${
                          snapshot.isDragging ? 'bg-blue-50' : ''
                        } ${item.isApiCall ? 'bg-gray-50' : ''}`}
                      >
                        <div className="flex-1 flex items-center">
                          <div
                            {...provided.dragHandleProps}
                            className="mr-2 cursor-grab text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                            </svg>
                          </div>
                          
                          {/* API call indicator */}
                          {item.isApiCall && (
                            <div className="mr-2 text-blue-500">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                            </div>
                          )}

                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-500">
                                {index + 1}.
                              </span>
                              {item.isApiCall ? (
                                <div>
                                  <span className="ml-2 text-sm font-medium text-gray-900">
                                    {formatApiCallNameForDisplay(item)}
                                  </span>
                                </div>
                              ) : item.action === 'workflow' ? (
                                <span className="text-sm font-medium text-blue-600">
                                  Run workflow: {item.workflowName}
                                  {item.repeatCount > 1 && <span className="ml-1 text-xs font-semibold bg-blue-100 text-blue-800 py-0.5 px-1.5 rounded">x{item.repeatCount}</span>}
                                </span>
                              ) : (
                                <span className="text-sm font-medium text-gray-900">
                                  {formatStepNameForDisplay(displayItem)}
                                </span>
                              )}
                            </div>

                            {/* Display additional details for non-workflow steps */}
                            {!item.isApiCall && item.action !== "workflow" && (
                              <div className="mt-1 ml-5 text-xs text-gray-500">
                                {item.action === "type" && (
                                  <>
                                    <div>Text: {item.secret ? "••••••••" : item.value}</div>
                                    <div>Element: {item.element}</div>
                                    {item.secret && (
                                      <div className="flex items-center mt-1 text-blue-600">
                                        <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                        Password field (value masked)
                                      </div>
                                    )}
                                  </>
                                )}
                                {item.action === "click" && (
                                  <div>Element: {item.exact ? `{{${item.element.replace(/^\{\{|\}\}$/g, '')}}}` : item.element}</div>
                                )}
                                {item.action === "navigate" && (
                                  <div>
                                    URL: {item.value}
                                    {item.value_mode === "fixture" && (
                                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 py-0.5 px-1.5 rounded">
                                        fixture
                                      </span>
                                    )}
                                  </div>
                                )}
                                {item.action === "assert" && (
                                  <div>Assertion: {resolveStepReferences(item.value)}</div>
                                )}
                              </div>
                            )}

                            {/* Display workflow ID for workflow steps */}
                            {!item.isApiCall && item.action === "workflow" && (
                              <div className="mt-1 ml-5 text-xs text-gray-500">
                                Workflow ID: {item.workflowId}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center">
                          {/* Edit button - different for API calls */}
                          {item.isApiCall ? (
                            <button
                              onClick={() => onEditApiCall(item)}
                              className="text-blue-600 hover:text-blue-800 p-1 mr-2"
                              title="Edit API call"
                              type="button"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          ) : (
                            <button
                              onClick={() => onEditStep(item)}
                              className="text-blue-600 hover:text-blue-800 p-1 mr-2"
                              title="Edit step"
                              type="button"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          
                          {/* Remove button - different for API calls */}
                          {item.isApiCall ? (
                            <button
                              onClick={() => onRemoveApiCall(item)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove API call"
                              type="button"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          ) : (
                            <button
                              onClick={() => onRemoveStep(item)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove step"
                              type="button"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}

export default SavedSteps; 